import { useEffect, useState } from "react";
import { DepartmentCountryCatalog, LocalizedCountryResolver } from "../addressLine";
import { COUNTRIES, type Country } from "../constants";
import { cx } from "../cx";
import type { FillMark } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { FieldShell } from "./fields";

interface Props {
  id: string;
  required?: boolean;
  error?: string;
  className?: string;
  fillStatus?: FillMark;
  countries?: Country[];
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

function countryChoices(
  query: string,
  selectedName: string,
  countries: Country[],
  localizedName: (code: string) => string,
): Country[] {
  const q = query.trim().toLowerCase();
  if (!q || q === selectedName.toLowerCase()) return countries;
  return countries.filter(
    (c) =>
      localizedName(c.c).toLowerCase().includes(q) ||
      c.n.toLowerCase().includes(q) ||
      c.c.toLowerCase() === q,
  );
}

export function CountrySelect({
  id,
  required,
  error,
  className,
  fillStatus,
  countries = COUNTRIES,
  value,
  onChange,
  onBlur,
}: Props) {
  const copy = useQuoteCopy();
  const catalog = new DepartmentCountryCatalog(countries);
  const resolver = new LocalizedCountryResolver(catalog, (code) => copy.countryName(code));
  const selectedName = copy.countryName(value);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selectedName);

  useEffect(() => {
    setDraft(selectedName);
  }, [selectedName]);

  const choices = open ? countryChoices(draft, selectedName, countries, (code) => copy.countryName(code)) : [];

  const select = (code: string, name: string) => {
    onChange(code);
    setDraft(name);
    setOpen(false);
    onBlur();
  };

  const commitDraft = () => {
    if (draft.trim().toLowerCase() === selectedName.trim().toLowerCase()) {
      setDraft(selectedName);
      setOpen(false);
      onBlur();
      return;
    }
    const code = resolver.toCode(draft);
    select(code, copy.countryName(code));
  };

  return (
    <FieldShell
      id={id}
      label={copy.t("route.country")}
      required={required}
      error={error}
      className={className}
      fillStatus={fillStatus}
    >
      <div className="nv-country-wrap">
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          autoComplete="off"
          placeholder={copy.t("route.selectCountry")}
          value={open ? draft : selectedName}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft();
          }}
          onFocus={() => {
            setDraft(selectedName);
            setOpen(true);
          }}
          onBlur={commitDraft}
        />
        {open && (
          <ul id={`${id}-list`} className="nv-country-list" role="listbox">
            {choices.map((c) => (
              <li key={c.c} className="nv-country-item" role="option" aria-selected={c.c === value}>
                <button
                  type="button"
                  className={cx("nv-country-option", c.c === value && "selected")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(c.c, copy.countryName(c.c))}
                >
                  {copy.countryName(c.c)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldShell>
  );
}
